import { useEffect, useRef, Fragment } from 'react';
import { Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { formatHora, formatSeconds } from '../../utils/dateUtils';
import { horarioPrevistoPonto, getColorByStatus, getBgColorByStatus } from '../../utils/tripUtils';
import { useMapData } from '../../hooks/map/useMapData';
import { useMapActions } from '../../hooks/map/useMapActions';
import { createPointIcon } from '../../utils/map/createPointIcon';
import { MAP_COLORS } from '../../utils/map/constants';
import MapLegend from './MapLegend';
import MapFocusHandler from './MapFocusHandler';
import MapReadyHandler from './MapReadyHandler';
import RealRouteDirection from './RealRouteDirection';
import MapPontosOperacionais from './MapPontosOperacionais';
import MapPezinhoVehiclesMarkers from './MapPezinhoVehiclesMarkers';
import {
    MapContainer,
    TileLayer,
    Polyline,
    Marker,
    Popup,
    Circle,
    Pane
} from 'react-leaflet';
import 'leaflet/dist/leaflet.css';

export default function MapView() {

    const { pointClick } = useMapActions();

    const {
        selectedPoint,
        pontosList,
        plannedTrajeto,
        realTrajeto,
        realPositions,
        pontosAnalysis,
        dataHoraIniTrip,
        lastPosition,
        situacao,
        mapFocus,
        mapReady,
        modeMap,
        pezinho
    } = useMapData();

    const theme = useTheme();
    const markerRefs = useRef({});
    const plannedPolylineRef = useRef(null);
    const realPolylineRef = useRef(null);

    const inferredBgColor = theme.palette.inferred.main;
    const inferredColor = theme.palette.inferred.contrastText;
    const sentidoDivergenteColor = theme.palette.warning.main;

    useEffect(() => {

        if (
            !selectedPoint ||
            mapFocus?.type !== 'point'
        ) {
            return;
        }

        Object.values(
            markerRefs.current
        ).forEach(marker => {

            marker?.closePopup();

        });

        const marker =
            markerRefs.current[
                selectedPoint.PONTO_UID
            ];

        marker?.openPopup();

    }, [
        selectedPoint,
        mapFocus?.type,
        mapFocus?.requestId
    ]);


    useEffect(() => {

        if (!pezinho?.selectedPositions?.length) {
            return;
        }

        Object.values(
            markerRefs.current
        ).forEach(marker => {

            marker?.closePopup();

        });

    }, [pezinho?.selectedPositions]);

    const normalIcon = createPointIcon();

    const selectedPointAddress = [
        selectedPoint?.pontoReferencia?.LOGRADOURO,
        selectedPoint?.pontoReferencia?.LOCALIZACAO
    ]
        .filter(Boolean)
        .join(' - ');

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                position: 'relative'
            }}
        >

            <MapContainer
                center={[-23.55052, -46.633308]}
                zoom={13}
                minZoom={11}
                maxZoom={18}
                zoomDelta={0.5}
                zoomSnap={0.5}
                wheelPxPerZoomLevel={160}
                zoomControl={false}
                attributionControl={false}
                preferCanvas
            >

                {/* ======================================== */}
                {/* MAPA */}
                {/* ======================================== */}

                <MapReadyHandler />
                <MapLegend
                    plannedPolylineRef={plannedPolylineRef}
                    realPolylineRef={realPolylineRef}
                    modeMap={modeMap}
                />
                <MapFocusHandler
                    mapFocus={mapFocus}
                    situacao={situacao}
                    selectedPoint={selectedPoint}
                    lastPosition={lastPosition}
                    mapReady={mapReady}
                    plannedTrajeto={plannedTrajeto}
                    pezinho={pezinho}
                />
                <TileLayer
                    url='https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png'
                />

                <MapPontosOperacionais />

                {/* ======================================== */}
                {/* TRAJETO */}
                {/* ======================================== */}

                {realTrajeto.length > 0 && (
                    <>
                        <Pane name="realRoute" style={{ zIndex: 390 }}>
                            <Polyline
                                ref={realPolylineRef}
                                positions={realTrajeto}
                                pathOptions={{
                                    color: MAP_COLORS.realRoute,
                                    weight: 9
                                }}
                            />
                        </Pane>
                        <Pane name="realRouteDirection" style={{ zIndex: 401 }}>
                            <RealRouteDirection
                                realPositions={realPositions}
                            />
                        </Pane>
                    </>                    
                )}

                {plannedTrajeto.length > 0 && (
                    <Pane name="plannedRoute" style={{ zIndex: 400 }}>
                        <Polyline
                            ref={plannedPolylineRef}
                            positions={plannedTrajeto}

                            pathOptions={{
                                color: MAP_COLORS.plannedRoute,
                                weight: 5
                            }}
                        />
                    </Pane>
                )}

                {/* ======================================== */}
                {/* PONTOS */}
                {/* ======================================== */}

                {pontosList.map((ponto, index) => {
                    const isFirst = index === 0;

                    const isLast = index === pontosList.length - 1;

                    let icon = normalIcon;

                    if (isFirst) {
                        icon = createPointIcon('start');
                    }

                    if (isLast) {
                        icon = createPointIcon('end');
                    }

                    const pontoReferencia = ponto?.pontoReferencia || null;

                    if(!pontoReferencia){
                        return null;
                    }

                    const analysis =
                        modeMap === 'trip'
                            ? pontosAnalysis?.[ponto.PONTO_REFERENCIA_ID]
                            : null;
                    
                    const passou = analysis?.passou && analysis?.horarioPassagem;

                    const labelPonto = analysis?.labelPonto;
                    const labelAtendimento = analysis?.labelAtendimento;
                    const passouInferido = analysis?.passouInferido === true;

                    const passagemInferida = passouInferido
                        ? analysis?.passagem
                        : null;

                    const distanciaPassagem = analysis?.distancia;
    
                    const horarioPassagem =
                        passou
                            ? formatHora(analysis.horarioPassagem, 'HH:mm')
                            : null;

                    const posMark = analysis?.posMark;

                    const velocidade = analysis?.velocidade;
    
                    const statusPonto = analysis?.statusPonto ?? 0;

                    // const sentidoDivergente = analysis?.sentidoDivergente;
                    const sentidoDivergente = false;

                    const raioOriginal =
                        analysis?.raioOriginal ??
                        Number(pontoReferencia?.RAIO) ??
                        0;

                    const raioUtilizado =
                        analysis?.raioUtilizado ??
                        raioOriginal;

                    const raioAjustado =
                        analysis?.raioAjustado === true;

                    const color = getColorByStatus(statusPonto, situacao, theme);
                    const bgColor = getBgColorByStatus(statusPonto, situacao, theme);

                    return (
                        <Fragment key={`${ponto.PONTO_REFERENCIA_ID}-${index}`}>
                            <Marker
                                ref={(marker) => {

                                    if (!marker) {
                                        return;
                                    }

                                    markerRefs.current[
                                        ponto.PONTO_UID
                                    ] = marker;

                                }}
                                position={[
                                    Number(pontoReferencia?.LATITUDE),
                                    Number(pontoReferencia?.LONGITUDE)
                                ]}
                                icon={icon}

                                zIndexOffset={2000}

                                interactive={!pezinho.loading}

                                eventHandlers={{
                                    click: () => {

                                        if (pezinho.loading) {
                                            return;
                                        }

                                        if (
                                            selectedPoint?.PONTO_UID ===
                                            ponto.PONTO_UID
                                        ) {
                                            return;
                                        }

                                        pointClick(ponto);

                                    }
                                }}
                            >
                                <Popup
                                    key={`${statusPonto}-${situacao}`}
                                    className={
                                        statusPonto
                                            ? 'point-status-popup'
                                            : ''
                                    }

                                    eventHandlers={{
                                        add: (e) => {

                                            const popupEl = e.target.getElement();

                                            if (!popupEl) {
                                                return;
                                            }

                                            popupEl.style.setProperty(
                                                '--popup-bg',
                                                bgColor
                                            );

                                            popupEl.style.setProperty(
                                                '--popup-color',
                                                color
                                            );
                                        }
                                    }}
                                >
                                    <p style={{ marginTop: 0, marginBottom: 0 }}>
                                        {isFirst && 'Inicio: '}
                                        {isLast && 'Fim: '}
                                        <strong>{pontoReferencia?.NOME}</strong>
                                    </p>

                                    {dataHoraIniTrip && (
                                        <p style={{ marginTop: 0, marginBottom: 0 }}>
                                            CHEGADA PREVISTA:{' '}
                                            <strong>
                                                {horarioPrevistoPonto(dataHoraIniTrip, ponto.TEMPO_CHEGADA)}
                                            </strong>
                                        </p>
                                    )}

                                    {horarioPassagem && (
                                        <p style={{ marginTop: 0, marginBottom: 0 }}>
                                            CHEGADA REAL:{' '}
                                            <strong>
                                                {horarioPassagem}
                                            </strong>
                                        </p>
                                    )}

                                    {labelPonto && (
                                        <p style={{ 
                                                marginTop: 8,
                                                marginBottom: 8,
                                                padding: 2,
                                                width: '100%',
                                                textAlign: 'center',
                                                borderWidth: 2,
                                                borderStyle: 'dashed',
                                                borderColor: color 
                                            }}>
                                            <strong>{labelPonto}</strong>
                                        </p>
                                    )}
                                    {raioUtilizado > 0 && (
                                        <p style={{ marginTop: 0, marginBottom: 0 }}>
                                            RAIO DE ENTRADA:{' '}
                                            <strong>
                                                {raioUtilizado}m
                                            </strong>
                                        </p>
                                    )}

                                    {raioAjustado && (
                                        <p
                                            style={{
                                                marginTop: 8,
                                                marginBottom: 8,
                                                padding: 4,
                                                width: '100%',
                                                textAlign: 'center',
                                                borderWidth: 2,
                                                borderStyle: 'dashed',
                                                borderColor: color,
                                                backgroundColor: sentidoDivergenteColor
                                            }}
                                        >
                                            <strong>
                                                RAIO AJUSTADO AUTOMATICAMENTE
                                                <br />
                                                {raioOriginal}m → {raioUtilizado}m
                                            </strong>
                                        </p>
                                    )}
                                    {distanciaPassagem != null && (
                                        <p style={{ marginTop: 0, marginBottom: 0 }}>
                                            PASSOU A:{' '}
                                            <strong>
                                                {distanciaPassagem}m
                                            </strong>
                                        </p>
                                    )}
                                    {labelAtendimento && (
                                        <p style={{ marginTop: 0, marginBottom: 0 }}>
                                            <strong>
                                                {labelAtendimento}
                                            </strong>
                                        </p>
                                    )}
                                    {sentidoDivergente && (
                                        <p style={{ 
                                            marginTop: 8,
                                            marginBottom: 8,
                                            padding: 2,
                                            width: '100%',
                                            textAlign: 'center',
                                            borderWidth: 2,
                                            borderStyle: 'dashed',
                                            borderColor: color,
                                            backgroundColor: sentidoDivergenteColor 
                                        }}>
                                            <strong>
                                                POSSÍVEL PASSAGEM
                                                <br />
                                                NO SENTIDO CONTRÁRIO
                                            </strong>
                                        </p>
                                    )}
                                </Popup>
                            </Marker>
                            {selectedPoint?.PONTO_UID === ponto.PONTO_UID && (
                                <>
                                    {/* Raio realmente utilizado pela análise */}
                                    <Circle
                                        key={`used-${statusPonto}-${raioUtilizado}`}
                                        center={[
                                            Number(pontoReferencia?.LATITUDE),
                                            Number(pontoReferencia?.LONGITUDE)
                                        ]}
                                        radius={raioUtilizado || 100}
                                        pathOptions={{
                                            color: statusPonto ? color : 'blue',
                                            fillColor: statusPonto ? bgColor : 'blue',
                                            fillOpacity: 0.25,
                                            weight: 2
                                        }}
                                    />

                                    {/* Raio originalmente cadastrado */}
                                    {raioAjustado && raioOriginal > 0 && (
                                        <Circle
                                            key={`original-${raioOriginal}`}
                                            center={[
                                                Number(pontoReferencia?.LATITUDE),
                                                Number(pontoReferencia?.LONGITUDE)
                                            ]}
                                            radius={raioOriginal}
                                            pathOptions={{
                                                color: sentidoDivergenteColor,
                                                fillOpacity: 0,
                                                weight: 2,
                                                dashArray: '6, 6'
                                            }}
                                        />
                                    )}
                                    {posMark && (
                                        <Marker
                                            position={[
                                                Number(posMark.latitude),
                                                Number(posMark.longitude)
                                            ]}
                                            icon={createPointIcon('posMark')}
                                        >
                                            <Popup
                                                className={
                                                    statusPonto
                                                        ? 'point-status-popup'
                                                        : ''
                                                }

                                                eventHandlers={{
                                                    add: (e) => {

                                                        const popupEl = e.target.getElement();

                                                        if (!popupEl) {
                                                            return;
                                                        }

                                                        popupEl.style.setProperty(
                                                            '--popup-bg',
                                                            bgColor
                                                        );

                                                        popupEl.style.setProperty(
                                                            '--popup-color',
                                                            color
                                                        );
                                                    }
                                                }}
                                            >
                                                <strong>MARCAÇÃO GPS</strong>
                                                <p style={{ margin: 0 }}>
                                                    PONTO:{' '}
                                                    <strong>
                                                        {pontoReferencia?.NOME}
                                                    </strong>
                                                </p>

                                                {horarioPassagem && (
                                                    <p style={{ marginTop: 0, marginBottom: 0 }}>
                                                        HORA:{' '}
                                                        <strong>
                                                            {horarioPassagem}
                                                        </strong>
                                                    </p>
                                                )}

                                                {distanciaPassagem != null && (
                                                    <p style={{ marginTop: 0, marginBottom: 0 }}>
                                                        DISTÂNCIA:{' '}
                                                        <strong>
                                                            {distanciaPassagem}m
                                                        </strong>
                                                    </p>
                                                )}

                                                {velocidade && (
                                                    <p style={{ margin: 0 }}>
                                                        VELOCIDADE:{' '}
                                                        <strong>
                                                            {velocidade} km/h
                                                        </strong>
                                                    </p>
                                                )}   
                                            </Popup>
                                        </Marker>
                                    )}                                    
                                </>                                
                            )}
                            {selectedPoint?.PONTO_UID === ponto.PONTO_UID && passagemInferida && (
                                <>
                                    {/* Posição GPS anterior */}
                                    <Marker
                                        position={[
                                            Number(passagemInferida.anterior.latitude),
                                            Number(passagemInferida.anterior.longitude)
                                        ]}
                                        icon={createPointIcon('inferenceGps')}
                                    >
                                        <Popup
                                            className={
                                                statusPonto
                                                    ? 'point-status-popup'
                                                    : ''
                                            }

                                            eventHandlers={{
                                                add: (e) => {

                                                    const popupEl = e.target.getElement();

                                                    if (!popupEl) {
                                                        return;
                                                    }

                                                    popupEl.style.setProperty(
                                                        '--popup-bg',
                                                        inferredBgColor
                                                    );

                                                    popupEl.style.setProperty(
                                                        '--popup-color',
                                                        inferredColor
                                                    );
                                                }
                                            }}
                                        >
                                            <strong>POSIÇÃO GPS ANTERIOR</strong>
                                            <p style={{ margin: 0 }}>
                                                PONTO:{' '}
                                                <strong>
                                                    {pontoReferencia?.NOME}
                                                </strong>
                                            </p>
                                            <p style={{ margin: 0 }}>
                                                HORA:{' '}
                                                <strong>
                                                    {formatHora(passagemInferida.anterior.dataHora)}
                                                </strong>
                                            </p>

                                            <p style={{ margin: 0 }}>
                                                VELOCIDADE:{' '}
                                                <strong>
                                                    {passagemInferida.anterior.velocidade} km/h
                                                </strong>
                                            </p>
                                            <p style={{ margin: 0 }}>
                                                DISTÂNCIA DO PONTO{' '}
                                                <strong>
                                                    {passagemInferida.anterior.distanciaPonto}m
                                                </strong>
                                            </p>
                                        </Popup>
                                    </Marker>

                                    {/* Posição GPS seguinte */}
                                    <Marker
                                        position={[
                                            Number(passagemInferida.seguinte.latitude),
                                            Number(passagemInferida.seguinte.longitude)
                                        ]}
                                        icon={createPointIcon('inferenceGps')}
                                    >
                                        <Popup
                                            className={
                                                statusPonto
                                                    ? 'point-status-popup'
                                                    : ''
                                            }

                                            eventHandlers={{
                                                add: (e) => {

                                                    const popupEl = e.target.getElement();

                                                    if (!popupEl) {
                                                        return;
                                                    }

                                                    popupEl.style.setProperty(
                                                        '--popup-bg',
                                                        inferredBgColor
                                                    );

                                                    popupEl.style.setProperty(
                                                        '--popup-color',
                                                        inferredColor
                                                    );
                                                }
                                            }}
                                        >
                                            <strong>POSIÇÃO GPS SEGUINTE</strong>
                                            <p style={{ margin: 0 }}>
                                                PONTO:{' '}
                                                <strong>
                                                    {pontoReferencia?.NOME}
                                                </strong>
                                            </p>

                                            <p style={{ margin: 0 }}>
                                                HORA:{' '}
                                                <strong>
                                                    {formatHora(passagemInferida.seguinte.dataHora)}
                                                </strong>
                                            </p>

                                            <p style={{ margin: 0 }}>
                                                VELOCIDADE:{' '}
                                                <strong>
                                                    {passagemInferida.seguinte.velocidade} km/h
                                                </strong>
                                            </p>
                                            <p style={{ margin: 0 }}>
                                                DISTÂNCIA DO PONTO{' '}
                                                <strong>
                                                    {passagemInferida.seguinte.distanciaPonto}m
                                                </strong>
                                            </p>
                                        </Popup>
                                    </Marker>

                                    {/* Posição Inferida */}
                                    <Marker
                                        position={[
                                            Number(passagemInferida.posicaoInferida.latitude),
                                            Number(passagemInferida.posicaoInferida.longitude)
                                        ]}
                                        icon={createPointIcon('inferred')}
                                    >
                                        <Popup
                                            className={
                                                statusPonto
                                                    ? 'point-status-popup'
                                                    : ''
                                            }

                                            eventHandlers={{
                                                add: (e) => {

                                                    const popupEl = e.target.getElement();

                                                    if (!popupEl) {
                                                        return;
                                                    }

                                                    popupEl.style.setProperty(
                                                        '--popup-bg',
                                                        inferredBgColor
                                                    );

                                                    popupEl.style.setProperty(
                                                        '--popup-color',
                                                        inferredColor
                                                    );
                                                }
                                            }}
                                        >
                                            <strong>ALVO</strong>
                                            <p style={{ margin: 0 }}>
                                                PONTO:{' '}
                                                <strong>
                                                    {pontoReferencia?.NOME}
                                                </strong>
                                            </p>

                                            <p style={{ margin: 0 }}>
                                                HORA:{' '}
                                                <strong>
                                                    {horarioPassagem}
                                                </strong>
                                            </p>

                                            <p style={{ margin: 0 }}>
                                                DISTÂNCIA DO PONTO:{' '}
                                                <strong>
                                                    {distanciaPassagem}m
                                                </strong>
                                            </p>

                                            <p style={{ margin: 0 }}>
                                                DISTÂNCIA ENTRE POSIÇÕES:{' '}
                                                <strong>
                                                    {Math.round(
                                                        passagemInferida.distanciaEntrePosicoes
                                                    )}m
                                                </strong>
                                            </p>

                                            <p style={{ margin: 0 }}>
                                                INTERVALO ENTRE POSIÇÕES:{' '}
                                                <strong>
                                                    {formatSeconds(passagemInferida.gapSeconds)}
                                                </strong>
                                            </p>

                                            <p style={{ margin: 0 }}>
                                                APÓS POSIÇÃO ANTERIOR:{' '}
                                                <strong>
                                                    {formatSeconds(passagemInferida.segundosDesdeAnterior)}
                                                </strong>
                                            </p>

                                            <p style={{ margin: 0 }}>
                                                ANTES DA POSIÇÃO SEGUINTE:{' '}
                                                <strong>
                                                    {formatSeconds(passagemInferida.segundosAteSeguinte)}
                                                </strong>
                                            </p>
                                        </Popup>
                                    </Marker>

                                    <Circle
                                        
                                        center={[
                                            Number(passagemInferida.posicaoInferida.latitude),
                                            Number(passagemInferida.posicaoInferida.longitude)
                                        ]}

                                        radius={Number(passagemInferida.posicaoInferida.distanciaPonto || 20)}

                                        pathOptions={{
                                            color: inferredColor,

                                            fillColor: inferredBgColor,

                                            fillOpacity: 0.25,
                                            weight: 2
                                        }}
                                    />

                                    {/* Segmento usado para fazer a inferência */}
                                    <Pane name={`segInfer-${ponto.PONTO_UID}`} style={{ zIndex: 401 }}>
                                        <Polyline
                                            positions={[
                                                [
                                                    Number(passagemInferida.anterior.latitude),
                                                    Number(passagemInferida.anterior.longitude)
                                                ],
                                                [
                                                    Number(passagemInferida.seguinte.latitude),
                                                    Number(passagemInferida.seguinte.longitude)
                                                ]
                                            ]}
                                            pathOptions={{
                                                color: inferredBgColor,
                                                weight: 5,
                                                dashArray: '10, 10'
                                            }}
                                        />
                                    </Pane>
                                    
                                </>
                            )}
                        </Fragment>                        
                    );
                })}

                {pezinho.active && (
                    <MapPezinhoVehiclesMarkers />
                )}

                {lastPosition && (
                    <Marker
                        position={[
                            Number(lastPosition.latitude),
                            Number(lastPosition.longitude)
                        ]}
                        zIndexOffset={1000}
                        icon={createPointIcon('vehicle')}
                    >
                        <Popup>
                            <strong>
                                {lastPosition.placa}
                            </strong>

                            <p style={{ marginTop: 0, marginBottom: 0 }}>
                                HORA:{' '}
                                <strong>
                                    {formatHora(lastPosition.dataHora)}
                                </strong>
                            </p>

                            <p style={{ marginTop: 0, marginBottom: 0 }}>
                                VELOCIDADE:{' '}
                                <strong>
                                    {lastPosition.velocidade}km/h
                                </strong>
                            </p>
                        </Popup>
                    </Marker>
                )}

            </MapContainer>

            {selectedPoint && selectedPointAddress && (
                <Box
                    sx={{
                        position: 'absolute',
                        right: 16,
                        bottom: 16,
                        zIndex: 1000,

                        maxWidth: 420,
                        px: 2,
                        py: 1.5,

                        bgcolor: 'background.paper',
                        color: 'text.primary',

                        borderRadius: 1,
                        boxShadow: 3,

                        pointerEvents: 'none'
                    }}
                >
                    <Box
                        sx={{
                            fontSize: 12,
                            color: 'text.secondary',
                            mb: 0.5
                        }}
                    >
                        LOCALIZAÇÃO
                    </Box>

                    <Box
                        sx={{
                            fontSize: 14,
                            fontWeight: 500
                        }}
                    >
                        {selectedPointAddress}
                    </Box>
                </Box>
            )}

        </Box>
    );
}